import { Modal } from '@mui/material';
import {
  FormAutocompleteMultiInput,
  FormTextInput,
  FormTimeInput,
  FormCheckbox,
  GlobalButton,
  LoaderBackdrop,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { usePickupCycleForm } from '@/pages/Business/pages/PickupCycles/hooks';
import FormOptionAutocomplete from '@/pages/Business/pages/PickupCycles/components/FormOptionAutocomplete';
import {
  BulkCreatePickupCyclePayload,
  PickupCycle,
  WEEK_DAYS,
} from '@/pages/Business/pages/PickupCycles/models';

interface PickupCycleModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: BulkCreatePickupCyclePayload) => void;
  initialData: PickupCycle | null;
  isSubmitting?: boolean;
  onConfigureAddress: (branchId: string) => void;
}

const PickupCycleModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isSubmitting = false,
  onConfigureAddress,
}: PickupCycleModalProps) => {
  const {
    control,
    onFormSubmit,
    commerceOptions,
    branchOptions,
    branchSingleOptions,
    isCommercesLoading,
    isBranchesLoading,
    selectedBranches,
    branchesWithoutAddress,
    selectedBranchCount,
    isUsingDefaultBranch,
    hasCommerceSelected,
    hasBranches,
    selectAllBranches,
    clearBranches,
    daysError,
    isEdit,
  } = usePickupCycleForm({ initialData, open, onSubmit });

  return (
    <Modal open={open} onClose={onClose}>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto'>
        <form
          onSubmit={onFormSubmit}
          className='flex flex-col p-6 gap-5 w-[520px] bg-white outline-none rounded-lg shadow-xl'
        >
          <h2 className='text-xl font-bold text-gray-800'>
            {isEdit ? 'Editar retiro' : 'Nuevo retiro'}
          </h2>

          <div className='flex flex-col gap-5'>
            <FormOptionAutocomplete
              name='commerceId'
              control={control}
              placeholder='Comercio'
              options={commerceOptions}
              loading={isCommercesLoading}
              required
            />

            <div className='flex flex-col gap-1'>
              {isEdit ? (
                // Editar es crear el retiro nuevo y borrar el viejo, así que
                // sigue siendo de a una sucursal.
                <FormOptionAutocomplete
                  name='branchId'
                  control={control}
                  placeholder='Sucursal'
                  options={branchSingleOptions}
                  loading={isBranchesLoading}
                  disabled={!hasCommerceSelected}
                />
              ) : (
                <>
                  <FormAutocompleteMultiInput
                    name='branchIds'
                    control={control}
                    placeholder='Sucursales'
                    options={branchOptions}
                    loading={isBranchesLoading}
                    disabled={!hasCommerceSelected}
                  />

                  {hasCommerceSelected && hasBranches && (
                    <div className='flex gap-3 mt-1 text-sm'>
                      <button
                        type='button'
                        className='underline text-[var(--primary-color)] hover:opacity-80'
                        onClick={selectAllBranches}
                      >
                        Todas las sucursales
                      </button>
                      <button
                        type='button'
                        className='underline text-gray-500 hover:opacity-80'
                        onClick={clearBranches}
                      >
                        Limpiar
                      </button>
                    </div>
                  )}
                </>
              )}

              {hasCommerceSelected && (
                <div className='mt-1 flex flex-col gap-1 text-sm'>
                  {isBranchesLoading && (
                    <span className='text-gray-400'>Cargando sucursales...</span>
                  )}

                  {!isBranchesLoading && !isEdit && (
                    <span className='text-gray-500'>
                      {isUsingDefaultBranch && selectedBranchCount <= 1
                        ? 'Se agenda un retiro desde la casa central.'
                        : `Se agendan ${selectedBranchCount} retiros, uno por sucursal.`}
                    </span>
                  )}

                  {!isBranchesLoading &&
                    selectedBranches.map((branch) => (
                      <span key={branch.id} className='text-gray-500'>
                        📍 {branch.name}
                      </span>
                    ))}

                  {!isBranchesLoading && branchesWithoutAddress.length > 0 && (
                    <div className='flex flex-col gap-1 text-red-600'>
                      <span>
                        ⚠️ Sin dirección no se genera el retiro. Falta cargarla en:
                      </span>
                      {branchesWithoutAddress.map((branch) => (
                        <button
                          key={branch.id}
                          type='button'
                          className='self-start underline text-[var(--primary-color)] hover:opacity-80'
                          onClick={() => onConfigureAddress(branch.id)}
                        >
                          {branch.name} — configurar ahora
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <FormTimeInput
              name='cycleTime'
              control={control}
              placeholder='Vuelta (hora)'
              required
            />

            <div className='flex flex-col gap-1'>
              <p className='text-sm font-medium text-gray-700'>Días</p>
              <div className='grid grid-cols-4 gap-x-2'>
                {WEEK_DAYS.map((day) => (
                  <FormCheckbox
                    key={day.key}
                    name={day.key}
                    control={control}
                    placeholder={day.label}
                  />
                ))}
              </div>
              {daysError && (
                <span className='text-sm text-red-600'>{daysError}</span>
              )}
            </div>

            <FormTextInput
              name='tag'
              control={control}
              placeholder='Etiqueta (opcional)'
            />
          </div>

          <div className='flex gap-3 justify-end mt-2'>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </GlobalButton>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={onFormSubmit}
              disabled={isSubmitting}
            >
              Guardar
            </GlobalButton>
          </div>
        </form>
        {isSubmitting && <LoaderBackdrop />}
      </div>
    </Modal>
  );
};

export default PickupCycleModal;
